import { useState } from "react";
import { CalculatorPage } from "../../components/CalculatorPage";
import { ResultExplainer } from "../../components/ResultExplainer";
import { FilingStatusSelector } from "../../components/FilingStatusSelector";
import { taxAPI, formatCurrency, formatPercent } from "../../lib/api";
import { buildCalculatorSchema } from "../../hooks/useSEO";
import { EmailGatePdfButton } from "../../components/EmailGatePdfButton";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const SeniorDeductionCalculator = () => {
  const [filing, setFiling] = useState("married_joint");
  const [age, setAge] = useState("68");
  const [spouseAge, setSpouseAge] = useState("66");
  const [magi, setMagi] = useState("90000");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await taxAPI.senior({
        age: Number(age),
        spouse_age: filing === "married_joint" ? Number(spouseAge) : 0,
        filing_status: filing,
        magi: Number(magi),
      });
      setResult(res);
    } catch (e) {
      toast.error("Calculation failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputPanel = (
    <div className="space-y-5">
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Filing Status</label>
        <FilingStatusSelector value={filing} onChange={setFiling} testid="senior-filing" />
      </div>
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Your Age</label>
        <input
          type="number"
          value={age}
          onChange={(e) => setAge(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-age"
        />
      </div>
      {filing === "married_joint" && (
        <div>
          <label className="font-body text-sm font-semibold block mb-2">Spouse Age</label>
          <input
            type="number"
            value={spouseAge}
            onChange={(e) => setSpouseAge(e.target.value)}
            className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
            data-testid="input-spouse-age"
          />
        </div>
      )}
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Modified AGI (MAGI)</label>
        <input
          type="number"
          value={magi}
          onChange={(e) => setMagi(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-magi"
        />
      </div>
      <button
        onClick={run}
        disabled={loading}
        data-testid="calculate-btn"
        className="w-full bg-primary text-primary-foreground font-mono uppercase tracking-widest text-sm px-6 py-4 hover:brightness-110 transition flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</> : "Calculate Senior Deduction"}
      </button>
    </div>
  );

  const resultPanel = result ? (
    <div className="space-y-6" data-testid="senior-result">
      {!result.eligible ? (
        <div className="border-2 border-foreground bg-muted p-6" data-testid="senior-ineligible">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground block mb-2">Not Eligible</span>
          <h3 className="font-heading text-2xl font-semibold">{result.reason}</h3>
          <p className="font-body text-sm text-muted-foreground mt-3">
            The OBBBA senior deduction is available only when you or your spouse (if filing jointly) is age 65 or older.
          </p>
        </div>
      ) : (
        <>
          <div className="border-2 border-foreground bg-foreground text-background p-6">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-background/60 mb-2 block">Your OBBBA Senior Deduction</span>
            <h3 className="font-heading text-3xl md:text-4xl font-bold leading-tight mb-2">
              You qualify for a <span className="text-primary">{formatCurrency(result.eligible_deduction)}</span> deduction.
            </h3>
            <p className="font-body text-sm text-background/80 mt-3">
              Estimated federal tax savings: <span className="font-mono font-semibold">{formatCurrency(result.estimated_federal_tax_savings)}</span> at your {formatPercent(result.marginal_rate_used, 0)} marginal rate.
            </p>
          </div>
          <div className="bg-card border-2 border-foreground overflow-hidden">
            <div className="p-5 border-b border-foreground/15 grid grid-cols-2 gap-4">
              <span className="font-body text-sm">Qualifying Persons 65+</span>
              <span className="font-mono tabular-nums text-right font-semibold">{result.qualifying_persons}</span>
            </div>
            <div className="p-5 border-b border-foreground/15 grid grid-cols-2 gap-4">
              <span className="font-body text-sm">Base Deduction ($6k each)</span>
              <span className="font-mono tabular-nums text-right">{formatCurrency(result.base_deduction)}</span>
            </div>
            <div className="p-5 border-b border-foreground/15 grid grid-cols-2 gap-4">
              <span className="font-body text-sm">Phase-Out Range</span>
              <span className="font-mono tabular-nums text-right text-sm">
                {formatCurrency(result.phaseout_start)}–{formatCurrency(result.phaseout_end)}
              </span>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              <span className="font-body text-sm">Phase-Out Applied</span>
              <span className={`font-mono text-right font-semibold ${result.phaseout_applied ? "text-destructive" : "text-primary"}`}>
                {result.phaseout_applied ? "Yes" : "No"}
              </span>
            </div>
          </div>
        </>
      )}
      <div className="bg-muted border border-foreground/15 p-5">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Note</span>
        <p className="font-body text-sm">{result.note || "OBBBA created an additional $6,000 above-the-line deduction for each qualifying senior (age 65+), available 2025-2028, with MAGI-based phase-out."}</p>
      </div>
      {result.eligible && (
        <EmailGatePdfButton
          testid="senior-pdf"
          filename="taxshift-2026-senior.pdf"
          pdfPayload={{
            title: "OBBBA Senior Deduction Analysis",
            subtitle: `Age: ${result.inputs.age}${result.inputs.filing_status === "married_joint" ? ` · Spouse: ${result.inputs.spouse_age}` : ""}`,
            inputs: [
              { label: "Your Age", value: result.inputs.age },
              { label: "Spouse Age", value: result.inputs.filing_status === "married_joint" ? result.inputs.spouse_age : "—" },
              { label: "MAGI", value: formatCurrency(result.inputs.magi) },
              { label: "Filing Status", value: result.inputs.filing_status.replace("_", " ").toUpperCase() },
            ],
            headline: `You qualify for a ${formatCurrency(result.eligible_deduction)} senior deduction — estimated federal savings ${formatCurrency(result.estimated_federal_tax_savings)}.`,
            rows: [
              { label: "Qualifying Persons (65+)", v2025: result.qualifying_persons, v2026: result.qualifying_persons },
              { label: "Base Deduction", v2025: formatCurrency(result.base_deduction), v2026: formatCurrency(result.base_deduction) },
              { label: "Eligible After Phase-Out", v2025: formatCurrency(result.eligible_deduction), v2026: formatCurrency(result.eligible_deduction) },
              { label: "Estimated Tax Savings", v2025: formatCurrency(result.estimated_federal_tax_savings), v2026: formatCurrency(result.estimated_federal_tax_savings) },
            ],
          }}
        />
      )}
      <ResultExplainer slug="senior-deduction-2026" />
    </div>
  ) : (
    <div className="border-2 border-dashed border-foreground/30 p-12 text-center bg-card/50" data-testid="senior-empty">
      <p className="font-heading text-xl">Enter your details and click Calculate.</p>
    </div>
  );

  return (
    <CalculatorPage
      title="Senior $6,000 Deduction Calculator (OBBBA 2026)"
      slug="senior-deduction-2026"
      overline="Calculator · OBBBA · Seniors"
      description="OBBBA created an additional $6,000 above-the-line deduction for each taxpayer age 65 or older, available 2025-2028. Phase-outs apply at higher income levels. This calculator computes your eligible deduction."
      seoTitle="Senior $6,000 Deduction Calculator 2026 — OBBBA 65+"
      seoKeywords="senior deduction 2026, 6000 senior deduction, obbba age 65, retiree tax deduction, senior tax benefit"
      jsonLd={buildCalculatorSchema({
        name: "OBBBA Senior Deduction Calculator",
        description: "Free calculator for OBBBA's $6,000 senior deduction (age 65+).",
        url: "/calculator/senior-deduction-2026",
      })}
      inputPanel={inputPanel}
      resultPanel={resultPanel}
      education={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-6">The OBBBA senior deduction</h2>
          <div className="font-body text-base leading-relaxed space-y-4 text-foreground/85">
            <p>
              OBBBA created a new temporary deduction of <strong>$6,000 per qualifying senior</strong> (age 65 or older),
              available for tax years 2025-2028. For a married couple where both spouses are 65+, the combined deduction
              is $12,000.
            </p>
            <h3 className="font-heading text-xl font-semibold mt-6">Phase-out</h3>
            <p>
              The deduction phases out at a rate of <strong>6% of MAGI</strong> above $75,000 (single) or $150,000 (married joint),
              reaching zero at $175,000 / $250,000 MAGI respectively. This makes it especially valuable for middle-income retirees.
            </p>
            <h3 className="font-heading text-xl font-semibold mt-6">Above-the-line</h3>
            <p>
              Unlike the traditional age-65 addition to the standard deduction, this new $6,000 deduction is above-the-line —
              meaning you can claim it even if you itemize.
            </p>
            <h3 className="font-heading text-xl font-semibold mt-6">Temporary</h3>
            <p>
              Unlike most OBBBA provisions that were made permanent, this deduction is scheduled to expire after 2028
              unless extended by future legislation.
            </p>
          </div>
        </>
      }
      relatedSlugs={[
        { slug: "standard-deduction-2026", label: "Standard Deduction" },
        { slug: "no-tax-on-tips", label: "No Tax on Tips" },
      ]}
    />
  );
};

export default SeniorDeductionCalculator;
